import Big from "big.js";
import darken from "polished/lib/color/darken";
import lighten from "polished/lib/color/lighten";
import readableColor from "polished/lib/color/readableColor";

interface RGBColor {
  a?: number;
  b: number;
  g: number;
  r: number;
}

export const CoreUtils = {
  dates: {
    getWeekdayIndex(day: string) {
      switch (day) {
        case "Monday":
          return 1;
        case "Tuesday":
          return 2;
        case "Wednesday":
          return 3;
        case "Thursday":
          return 4;
        case "Friday":
          return 5;
        case "Saturday":
          return 6;
        case "Sunday":
          return 7;
        default:
          throw new Error("ERROR WEEKDAY");
          return 0;
      }
    },
    getNextWeekday(day: string) {
      switch (day) {
        case "Sunday":
          return "Monday";
        case "Monday":
          return "Tuesday";
        case "Tuesday":
          return "Wednesday";
        case "Wednesday":
          return "Thursday";
        case "Thursday":
          return "Friday";
        case "Friday":
          return "Saturday";
        case "Saturday":
          return "Sunday";
        default:
          return "Monday";
      }
    },
    // tslint:disable-next-line
    getPreviousWeekday(day: string) {
      switch (day) {
        case "Sunday":
          return "Saturday";
        case "Monday":
          return "Sunday";
        case "Tuesday":
          return "Monday";
        case "Wednesday":
          return "Tuesday";
        case "Thursday":
          return "Wednesday";
        case "Friday":
          return "Thursday";
        case "Saturday":
          return "Friday";
        default:
          return "Monday";
      }
    },
    getMillisFromMinutes(minutes: number) {
      return 1000 * 60 * minutes;
    },
  },
  string: {
    remove_linebreaks: (str: string): string => {
      return str.replace(/(\r\n\t|\n|\r\t)/gm, "");
    },
    replace_spaces: (string: string, replace: string) => {
      return string.replace(/ /g, replace);
    },
    cap_first: (string: string) => string.charAt(0).toUpperCase() + string.slice(1),
  },
  currency: {
    centsToDecimal(amount: number | string, precision: number) {
      const ratio = Math.pow(10, precision);
      return new Big(amount).div(ratio).toFixed(precision);
    },
    decimalToCents(amount: number | string, precision: number) {
      return parseInt(new Big(amount).times(Math.pow(10, precision)).toFixed(0), 10);
    },
    precision_to_step: (precision: number) => {
      if (precision === 0) return "1";
      if (precision === 1) return "0.1";
      if (precision === 2) return "0.01";
      if (precision === 3) return "0.001";
      if (precision === 4) return "0.0001";
      return "0.01";
    },
  },
  colors: {
    shadeAlternate(shade: number, color: string) {
      if (readableColor(color) === "#000") {
        return darken(shade, color);
      }
      else {
        return lighten(shade, color);
      }
    },
    stringFromRgba(obj: RGBColor) {
      return `rgba(${obj.r}, ${obj.g}, ${obj.b}, ${obj.a})`;
    },
    rgbaObjectFromString(string: string): RGBColor {
      if (!string) return {r: 0, g: 0, b: 0, a: 1};
      if (string.indexOf("rgba") !== -1) {
        const temp = (string.split("rgba(")[1]).slice(0, -1).split(",");
        return {
          r: parseInt(temp[0], 10),
          g: parseInt(temp[1], 10),
          b: parseInt(temp[2], 10),
          a: parseFloat(temp[3]),
        };
      }
      else {
        const temp: string | string[] = (string.split("rgb(")[1]).slice(0, -1).split(",");
        return {
          r: parseInt(temp[0], 10),
          g: parseInt(temp[1], 10),
          b: parseInt(temp[2], 10),
          a: 1,
        };
      }
    },
  },
  image: {
    uc: (image?: BusinessImage | null, transformations?: ImageTransformations) => {

      if (!image || !image._id)
        return undefined;

      if (image.not_uc) {
        return image._id;
      }

      let url = `https://ucarecdn.com/${image._id}`;

      if (image.modifiers) {
        url += `/${image.modifiers}`;
      }

      if (transformations) {
        for (const key in transformations) {
          if (transformations.hasOwnProperty(key)) {
            const value = transformations[key as keyof ImageTransformations];
            if (value === "scale_crop" || value === "crop") {
              url += `/-/${key}/${value}/center`;
            }
            else {
              url += `/-/${key}/${value}`;
            }
          }
        }
      }

      url += `/-/progressive/yes`;
      url = url + `/${image.name}`;
      return url;

    },
  },
  ui: {
    breakpoint(width: number) {
      if (width >= 1200)
        return "xl";
      else if (width >= 920)
        return "lg";
      else if (width >= 720)
        return "md";
      else if (width >= 480)
        return "sm";
      return "xs";
    },
    breakpoints: {
      xl: 1200,
      lg: 920,
      md: 720,
      sm: 480,
    },
  },
  fns: {
    wait(duration: number) {
      return new Promise( (resolve) => {
        setTimeout(() => resolve(), duration);
      });
    },
  },
};
